/**
 * @prettier
 */

import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { notDesktop } from '@zola-helpers/client';
import { cloneSampleProject as cloneSampleProjectAction } from '../../../../actions/projectActions';
import * as actions from '../../../../reducers/pdp/actions';
import * as selectors from '../../../../reducers/pdp/selectors';
import { getPricing } from '../../../../reducers/pricing/selectors';
import { AllOptionsSection } from '../../../CardDetail/AllOptionsSection';
import Loader from '../../../../../components/common/ui/Loader';
import { QtyPriceSection } from '../../../CardDetail/DetailQtyPriced';
import { getStepUrl } from '../../../../util/getStepUrl';

import styles from './styles.module.less';

const ProjectCloneModal = ({
  activeVariationUUID,
  cloneSampleProject,
  cost,
  fetchProjectCardSuiteDetails,
  hideModalFn,
  isBusy,
  pageData,
  project: { project_uuid: projectUUID, project_name: projectName },
  quantity,
  reopenCurrentModal,
  setActiveCardSuite,
  setQuantity,
  setOptionValue,
  medium,
}) => {
  const [isCloningProject, setIsCloningProject] = useState(false);
  const [initializedQtyToMin, setInitializedQtyToMin] = useState(false);
  const isMobileOrTablet = notDesktop();

  useEffect(() => {
    // Don't refetch the project's card suite details if they're already loaded
    if (!projectUUID || pageData.name === projectName) return;

    fetchProjectCardSuiteDetails(projectUUID).then(cardSuiteUUID => {
      if (cardSuiteUUID) {
        setActiveCardSuite(cardSuiteUUID);
        if (medium) {
          // The 'type' attribute of the card variation options refers to the card medium, not the card type in the PDP options
          // We set the 'type' option value to the medium of the reference card of the component initiating this modal
          setOptionValue({
            type: 'type',
            value: medium.toLowerCase(),
          });
        }
      }
    });
  }, [projectUUID, pageData.name]);

  const handleCustomizeClick = useCallback(() => {
    setIsCloningProject(true);
    cloneSampleProject(projectUUID, activeVariationUUID, quantity)
      .then(newProject => {
        browserHistory.push(getStepUrl(newProject.uuid));
        hideModalFn();
      })
      .catch(() => {
        setIsCloningProject(false);
      });
  }, [
    activeVariationUUID,
    cloneSampleProject,
    hideModalFn,
    projectUUID,
    quantity,
    setIsCloningProject,
  ]);

  // When users clone a sample project, we don't want to use the default quantity (1) from the BE.
  // Instead, we reset the quantity to the minimum "non-sample" amount (typically 20 or 50).
  useEffect(() => {
    // Only reset the quantity once
    if (!initializedQtyToMin && pageData && pageData.paperInfo) {
      setInitializedQtyToMin(true);
      setQuantity(pageData.paperInfo.qty_min);
    }
  }, [pageData, initializedQtyToMin, setQuantity]);

  if (isBusy) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader />
      </div>
    );
  }

  const { paperInfo } = pageData;

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.drawerBody}>
        <div className={styles.drawerTitle}>More Ways to Customize Your Design</div>
        <div className={styles.drawerSubtitle}>Make your cards as unique as you two.</div>
        <AllOptionsSection
          isAdminView={false}
          location="ORDER_MORE_MODAL"
          paperTypeModalProps={{ hideProofBanner: true }}
          paperTypeModalOptions={{
            onBack: reopenCurrentModal,
            hideClose: true,
            disableBackgroundClick: true,
            size: 'lg',
            showInDrawer: true,
          }}
        />
      </div>
      <div className={styles.drawerButtons}>
        <QtyPriceSection
          proofAllowed={paperInfo.qty_single_allowed}
          cost={cost}
          defaultSelection={paperInfo.qty_min}
          increment={paperInfo.qty_increment}
          max={paperInfo.qty_max}
          min={paperInfo.qty_min}
          onSelect={setQuantity}
          hideTitle={isMobileOrTablet}
          handleCustomizeClick={handleCustomizeClick}
          disableCustomizeButton={!activeVariationUUID || isCloningProject}
          showDropdownOptionsOnTop
        />
      </div>
    </div>
  );
};

const mapsStateToProps = state => ({
  activeVariationUUID: selectors.getActiveVariationUUID(state),
  cost: selectors.getCostData(state),
  isBusy: selectors.getIsBusy(state),
  pageData: selectors.getPageData(state),
  pricing: getPricing(state),
  quantity: selectors.getQuantity(state),
});

const mapDispatchToProps = {
  fetchProjectCardSuiteDetails: actions.fetchCardSuiteByProjectId,
  setActiveCardSuite: actions.setActiveSuite,
  setQuantity: actions.setQuantity,
  setOptionValue: actions.setOptionValue,
  cloneSampleProject: cloneSampleProjectAction,
};

export default connect(mapsStateToProps, mapDispatchToProps)(ProjectCloneModal);
